import toEchartData from './dataProcessor';
import { wrapWithMaxWidth } from '../../tooltipUtils';

/**
 * Pinta um nó de pergunta (level 3) com a cor de `colorByPergunta[node.path]` e propaga
 * a mesma cor aos filhos como cor-base — a saturação por nível definida em `levels`
 * gera os tons variados nas folhas (alternativas correlacionadas).
 */
function paintPerguntaNodes(rootNodes, colorByPergunta) {
  if (!Array.isArray(rootNodes)) return rootNodes;
  return rootNodes.map((alternativaPrincipal) => ({
    ...alternativaPrincipal,
    children: Array.isArray(alternativaPrincipal.children)
      ? alternativaPrincipal.children.map((pergunta) => {
        const color = colorByPergunta[pergunta.path];
        if (!color) return pergunta;
        return {
          ...pergunta,
          itemStyle: { ...(pergunta.itemStyle || {}), color },
          children: Array.isArray(pergunta.children)
            ? pergunta.children.map((alt) => ({
              ...alt,
              itemStyle: { ...(alt.itemStyle || {}), color },
            }))
            : pergunta.children,
        };
      })
      : alternativaPrincipal.children,
  }));
}

function applyHighlitToPergunta(rootNodes, perguntaEnunciado, theme) {
  if (!perguntaEnunciado) return rootNodes;
  const highlitStyle = {
    borderType: theme && theme.highlitedItemBorderType,
    borderColor: theme && theme.highlitedItemBorderColor,
    borderWidth: theme && theme.highlitedItemBorderWidth,
  };

  return rootNodes.map((alternativaPrincipal) => ({
    ...alternativaPrincipal,
    children: Array.isArray(alternativaPrincipal.children)
      ? alternativaPrincipal.children.map((pergunta) => {
        const isMatch = pergunta.path === perguntaEnunciado;
        if (!isMatch) {
          return {
            ...pergunta,
            itemStyle: { ...(pergunta.itemStyle || {}), borderColor: undefined, borderWidth: undefined, borderType: undefined },
          };
        }
        return {
          ...pergunta,
          itemStyle: { ...(pergunta.itemStyle || {}), ...highlitStyle },
          children: Array.isArray(pergunta.children)
            ? pergunta.children.map((alt) => ({
              ...alt,
              itemStyle: { ...(alt.itemStyle || {}), ...highlitStyle },
            }))
            : pergunta.children,
        };
      })
      : alternativaPrincipal.children,
  }));
}

function tooltipFormatter(info) {
  const value = info.data.correlacao;
  const { treePathInfo } = info;
  const treePath = [];

  for (let i = 1; i < treePathInfo.length - 1; i++) {
    const node = treePathInfo[i];
    treePath.push(`<strong>${node.name}</strong>`);
  }

  const arrow = '<svg width="10" height="10" viewBox="0 0 24 24" style="vertical-align: middle; fill: white" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M8.122 24l-4.122-4 8-8-8-8 4.122-4 11.878 12z"/></svg>';

  const content = [
    `<div class="tooltip-title">${treePath.join(` ${arrow} `)}</div><br/>`,
    `${info.marker}<strong>${info.data.name} + </strong><br/>`,
    `correlação: ${value}`,
  ].join('');

  return wrapWithMaxWidth(content);
}

function toSeries(seriesData, fontSize) {
  return [{
    name: 'Correlação',
    type: 'treemap',
    visibleMin: 50,
    nodeClick: false,
    label: {
      show: true,
      formatter: '{b}',
      fontWeight: 'bolder',
      fontSize,
      backgroundColor: 'rgba(0,0,0,0.3)',
    },
    itemStyle: {
      borderColor: '#fff',
    },
    tooltip: {
      confine: true,
    },
    data: seriesData,
    levels: [
      {
        itemStyle: {
          borderWidth: 0,
          gapWidth: 5,
        },
      },
      {
        itemStyle: {
          gapWidth: 1,
        },
      },
      {
        colorAlpha: [0.85, 1],
        itemStyle: {
          gapWidth: 1,
          borderColorSaturation: 0.6,
        },
      },
    ],
  }];
}

/**
 * Constrói o echart option do treemap de correlação no contrato consumido pelo ChartManager.
 * @param {object} data Dado bruto da correlação (alternativas com perguntas correlacionadas)
 * @param {{colors: string[], highlitedItemBorderType?, highlitedItemBorderColor?, highlitedItemBorderWidth?}} colors
 *   Tema parsado (mesmo formato consumido pelos outros gráficos): contém o array `colors`
 *   ordenado pelas perguntas correlacionadas.
 * @param {number} fontSize Tamanho de fonte (label dos nós)
 * @param {Array<string>} _disabledLegends Legendas desabilitadas (não usado por enquanto)
 * @param {number} highlitIndex Índice da pergunta highlightada (para realce no double click)
 * @returns {object} echart option completa para o treemap
 */
export default function toTreeMapEchartOption(data, colors, fontSize, _disabledLegends, highlitIndex) {
  const echartData = toEchartData(data);
  let seriesData = echartData.seriesData;
  const perguntas = echartData.legends;

  const themeColors = (colors && colors.colors) || [];

  const colorByPergunta = perguntas.reduce((acc, perguntaEnunciado, index) => {
    if (themeColors[index]) acc[perguntaEnunciado] = themeColors[index];
    return acc;
  }, {});

  seriesData = paintPerguntaNodes(seriesData, colorByPergunta);

  if (typeof highlitIndex === 'number' && highlitIndex >= 0) {
    const perguntaEnunciado = perguntas[highlitIndex];
    seriesData = applyHighlitToPergunta(seriesData, perguntaEnunciado, colors);
  }

  return {
    color: themeColors,
    legend: {
      data: perguntas,
      show: false,
    },
    tooltip: {
      formatter: tooltipFormatter,
      confine: true,
    },
    series: toSeries(seriesData, fontSize),
  };
}
